// Load the static-hair PLY into a three `GaussianSplat` and the manifest JSON.
//
// PLY layout is the standard 3DGS binary (packPly.js), which three's own
// `GaussianSplatPLYLoader` parses — so this needs no Spark and no decoder of
// ours. Gaussians are stored in strand-major order (gaussian i = strand i//N,
// sample i%N); that ordering is a CONTRACT asserted via the manifest's
// `strand_order` flag, because spec_mod rebuilds the (S_pad, N_pad) grid from
// the flat array.
//
// THE LAYOUT LINES UP WITH WHAT THE SHADE ALREADY WROTE, which is what made this
// a swap rather than a rewrite. three stores colour as `Uint8ClampedArray` RGBA
// bytes; read as a `Uint32Array` on a little-endian machine that is R in bits
// 0..7, G in 8..15, B in 16..23 and alpha in 24..31 — byte for byte the layout
// Spark used for word 0, and exactly what the WGSL shade's `pack4x8unorm`
// already emits. So `mergePackedRgb` is unchanged and the readback still lands
// with one u32 loop and no repacking.
//
// Positions get SIMPLER: three keeps centres as plain f32, so the half-float
// decode the packed layout needed is gone (`extractPositionsFromGeometry`).

import { GaussianSplat } from '../ogs/render/three/threeSplatDeps.js';

// cache:'no-store' — the hair PLY is ~150 MB; Chrome's HTTP cache refuses to
// write a file that large and the fetch fails with ERR_CACHE_WRITE_FAILURE,
// which crashed the first load (then a retry succeeded → flickering UX). Not
// caching avoids the write entirely; the file streams straight to memory. This
// is also why the bytes are fetched here and handed to `parse()` rather than
// letting the loader fetch the URL itself — three's `FileLoader` would go
// through the cache and hit the same wall.
async function fetchBytes(url) {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`fetch ${url}: HTTP ${res.status}`);
  return await res.arrayBuffer();
}

async function fetchJson(url) {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`fetch ${url}: HTTP ${res.status}`);
  return await res.json();
}

/** Returns { mesh, manifest }.
 *  - `mesh` is a `GaussianSplat` whose geometry carries position / covariance /
 *    color; the caller adds it to the scene and sets `.visible`.
 *  - `manifest` is the parsed hair_static.json. */
export async function loadHairPly(baseUrl) {
  const [plyBytes, manifest] = await Promise.all([
    fetchBytes(`${baseUrl}/hair_static.ply`),
    fetchJson(`${baseUrl}/hair_static.json`),
  ]);
  if (!manifest.strand_order) {
    throw new Error('[hair] manifest.strand_order=false — spec_mod requires strand-ordered Gaussians');
  }
  const { GaussianSplatPLYLoader } = await import('three/examples/jsm/loaders/GaussianSplatPLYLoader.js');
  const geometry = new GaussianSplatPLYLoader().parse(plyBytes);
  const count = geometry.getAttribute('position').count;
  if (count !== manifest.P) {
    console.warn(`[hair] splat count mismatch: ply has ${count}, manifest says ${manifest.P}`);
  }
  // `autoSort` off: the shade rewrites COLOUR only, never a centre, so the
  // depth order is settled once and a per-frame re-sort of ~549k splats would
  // buy nothing. A head-follow move is a MATRIX change, which three re-sorts on
  // anyway because the view direction into the mesh changes.
  const mesh = new GaussianSplat(geometry, { autoSort: true });
  mesh.numSplats = count;
  return { mesh, manifest };
}

/** Fetch const_latent.bin (256*8*8 f32 = 65536 floats) as a Float32Array. */
export async function loadConstLatent(baseUrl) {
  const buf = await fetchBytes(`${baseUrl}/const_latent.bin`);
  return new Float32Array(buf);
}
